// src/experiments/remoteConfig.js
//
// Remote control: one document in Firebase Realtime Database, read over its
// plain REST API with a single fetch and no SDK, so an experiment can be
// started, ramped, killed or shipped, and a feature flag flipped, from the
// Firebase console without a deploy. The document has two sections:
//
//   { "experiments": { "<key>": { "traffic", "force", "hard" } },
//     "flags":       { "<key>": <value matching the code default> } }
//
// Each section is validated on its own against the code: a malformed entry
// or a forbidden field rejects that section and the in-code values stand
// for it, while the other section still applies. Keys that are not in code
// are ignored with a warning, so deleting something from code never silently
// disables the rest. The page never waits on the fetch.
//
// Timing: a validated document is applied on the next route change, never
// mid-render. The last valid document is cached in localStorage and applied
// synchronously at boot when it is under 24 hours old, so a returning visitor
// sees a kill at first paint.

import * as Sentry from '@sentry/react';
import { groupOf, isActive, variantsOf } from './assign';
import { invalidateAssignments } from './assignments';
import { EXPERIMENTS } from './experiments.config';
import { FLAGS } from './flags.config';
import {
  areFlagsSettled,
  markFlagsSettled,
  setRemoteFlags,
  validateFlags,
} from './flags';
import { setExperiments } from './registry';
import { forgetExposure } from './sessionExposures';
import { ensureUid } from './uid';

// The project's default instance in europe-west1. Public by design: the rules
// allow anyone to read /config and nobody to write it. Hardcoded rather than
// read from .env because the production build is not ours and may not carry
// our variables; the env var is an optional override only.
const DEFAULT_RTDB_URL =
  'https://hippo-staging-85266-default-rtdb.europe-west1.firebasedatabase.app';
export const RTDB_URL = (
  process.env.REACT_APP_EXPERIMENTS_RTDB_URL || DEFAULT_RTDB_URL
).replace(/\/+$/, '');
export const REMOTE_PATH = '/config.json';
export const CACHE_KEY = 'hippo_exp_remote';
export const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const FETCH_TIMEOUT_MS = 5000;
const ALLOWED_FIELDS = ['traffic', 'force', 'hard'];
const SECTIONS = ['experiments', 'flags'];

let pending = null;

// A session can outlive its boot fetch by a long way: a quote flow is one
// page load that may run for twenty minutes, and a parked tab for hours.
// Without a re-fetch, a kill issued in that window would never reach them.
// The document is therefore re-fetched on navigation and when a tab comes
// back to the foreground, throttled so neither can stampede. Applying is
// unchanged: a newer document waits for the next route change, so nothing
// shifts underneath a page someone is reading.
export const REFETCH_MIN_INTERVAL_MS = 60000;

// The throttle lives in sessionStorage, not just in memory, so it survives a
// full page load. Several paths deliberately reload the app (logout, "no,
// that's not me", "first time quoting"), and some links navigate the old way,
// and each of those would otherwise start a fresh fetch no matter how recently
// one ran. Those three reset paths also clear sessionStorage, so they clear
// this with it and their reload fetches properly, which is exactly right.
export const FETCH_THROTTLE_KEY = 'hippo_exp_fetched_at';
let memoryLastFetchStartedAt = 0;

function readLastFetchStartedAt() {
  try {
    const raw = sessionStorage.getItem(FETCH_THROTTLE_KEY);
    if (raw !== null) {
      const at = Number(raw);
      return Number.isFinite(at) && at > 0 ? at : 0;
    }
  } catch {
    // storage blocked: fall back to memory, which at least throttles within
    // this page load
  }
  return memoryLastFetchStartedAt;
}

function writeLastFetchStartedAt(at) {
  memoryLastFetchStartedAt = at;
  try {
    sessionStorage.setItem(FETCH_THROTTLE_KEY, String(at));
  } catch {
    // storage blocked
  }
}

// What happened with the remote document, for the debug overlay.
const status = {
  fetch: 'idle', // idle | ok | partial | rejected | failed
  message: '',
  fetchedAt: null,
  ignored: [],
  applied: 'code', // code | cached | remote
  experiments: 'none', // none | ok | rejected
  flags: 'none',
};

export function getRemoteStatus() {
  return { ...status, url: `${RTDB_URL}${REMOTE_PATH}`, pending: !!pending };
}

const warned = new Set();
function warnOnce(id, message) {
  if (warned.has(id)) return;
  warned.add(id);
  try {
    Sentry.addBreadcrumb({
      category: 'experiments',
      level: 'warning',
      message,
    });
  } catch {
    // Sentry unavailable
  }
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[experiments] ${message}`);
  }
}

// Validates the `experiments` section against the in-code registry. Returns
// { ok: true, entries, ignored } with normalised entries, or
// { ok: false, errors }. Unknown keys land in `ignored`, never in `errors`.
export function validateRemoteDocument(doc, experiments = EXPERIMENTS) {
  if (doc === null || doc === undefined) {
    return { ok: true, entries: {}, ignored: [] };
  }
  if (typeof doc !== 'object' || Array.isArray(doc)) {
    return { ok: false, errors: ['experiments must be an object'] };
  }

  const errors = [];
  const ignored = [];
  const entries = {};
  const byKey = new Map(experiments.map((e) => [e.key, e]));

  for (const [key, raw] of Object.entries(doc)) {
    const experiment = byKey.get(key);
    if (!experiment) {
      ignored.push(key);
      continue;
    }
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      errors.push(`"${key}": entry must be an object`);
      continue;
    }
    for (const field of Object.keys(raw)) {
      if (!ALLOWED_FIELDS.includes(field)) {
        errors.push(`"${key}": unknown field "${field}"`);
      }
    }

    const entry = {};
    if (raw.traffic !== undefined) {
      const traffic = raw.traffic;
      if (
        typeof traffic !== 'number' ||
        !Number.isFinite(traffic) ||
        traffic < 0 ||
        traffic > 1
      ) {
        errors.push(`"${key}": traffic must be a number in [0, 1]`);
      } else {
        entry.traffic = traffic;
      }
    }
    if (raw.force !== undefined) {
      const known = variantsOf(experiment).some((v) => v.key === raw.force);
      if (typeof raw.force !== 'string' || !known) {
        errors.push(`"${key}": force must name a variant defined in code`);
      } else {
        entry.force = raw.force;
      }
    }
    if (raw.hard !== undefined) {
      if (typeof raw.hard !== 'boolean') {
        errors.push(`"${key}": hard must be true or false`);
      } else if (raw.force === undefined) {
        errors.push(`"${key}": hard is only valid alongside force`);
      } else {
        entry.hard = raw.hard;
      }
    }
    entries[key] = entry;
  }

  // The merged state may not leave two experiments active in one group.
  if (!errors.length) {
    const activeGroups = new Map();
    for (const experiment of experiments) {
      const entry = entries[experiment.key] || {};
      const traffic = entry.traffic ?? experiment.traffic;
      const control = variantsOf(experiment)[0]?.key;
      const active = entry.force
        ? entry.force !== control
        : isActive({ traffic });
      if (!active) continue;
      const group = groupOf(experiment);
      if (activeGroups.has(group)) {
        errors.push(
          `"${experiment.key}": group "${group}" would also have "${activeGroups.get(group)}" active`
        );
      } else {
        activeGroups.set(group, experiment.key);
      }
    }
  }

  return errors.length ? { ok: false, errors } : { ok: true, entries, ignored };
}

// Validates the whole document: each section on its own, unknown top-level
// keys ignored. A document that is not an object rejects both sections.
export function validateConfigDocument(
  doc,
  { experiments = EXPERIMENTS, flags = FLAGS } = {}
) {
  if (doc === null || doc === undefined) {
    return {
      experiments: { ok: true, entries: {}, ignored: [] },
      flags: { ok: true, entries: {}, ignored: [] },
      ignored: [],
    };
  }
  if (typeof doc !== 'object' || Array.isArray(doc)) {
    const errors = ['document must be an object with experiments and flags'];
    return {
      experiments: { ok: false, errors },
      flags: { ok: false, errors },
      ignored: [],
    };
  }
  return {
    experiments: validateRemoteDocument(doc.experiments, experiments),
    flags: validateFlags(doc.flags, flags),
    ignored: Object.keys(doc).filter((key) => !SECTIONS.includes(key)),
  };
}

// A document is rejected whole when every section it actually contains is
// invalid. Absent sections are fine (they mean "no overrides"), but they must
// not turn a document made only of bad sections into a partial success.
function outcomeOf(doc, validated) {
  const present = SECTIONS.filter(
    (section) => doc && typeof doc === 'object' && doc[section] !== undefined
  );
  const okPresent = present.filter((section) => validated[section].ok);
  const rejected = present.length > 0 && okPresent.length === 0;
  const partial = okPresent.length > 0 && okPresent.length < present.length;
  return { rejected, partial };
}

// Merges validated experiment entries over the in-code registry. Returns the
// effective list plus the per-key meta the exposure event records.
export function mergeRemote(entries, source, experiments = EXPERIMENTS) {
  const list = experiments.map((experiment) => {
    const entry = entries[experiment.key];
    return entry && entry.traffic !== undefined
      ? { ...experiment, traffic: entry.traffic }
      : experiment;
  });
  const meta = {};
  for (const [key, entry] of Object.entries(entries)) {
    meta[key] = {
      source,
      force: entry.force ?? 'none',
      hard: Boolean(entry.hard),
    };
  }
  return { list, meta };
}

function applyExperiments({ list, meta }, source) {
  // A hard force must reach visitors mid-session: forget the arm they saw so
  // the session freeze does not keep them in it.
  for (const [key, entry] of Object.entries(meta)) {
    if (entry.hard) forgetExposure(key);
  }
  setExperiments(list, meta);
  invalidateAssignments(false);
  status.applied = source;
}

// Applies every valid section of a validated document. Returns whether
// anything was applied. `notify` is false during a route change.
function applySections(validated, source, experiments, notify) {
  let applied = false;
  const messages = [];
  if (validated.experiments.ok) {
    applyExperiments(
      mergeRemote(validated.experiments.entries, source, experiments),
      source
    );
    status.experiments = 'ok';
    applied = true;
  } else {
    status.experiments = 'rejected';
    messages.push(
      `experiments rejected: ${validated.experiments.errors.join('; ')}`
    );
  }
  if (validated.flags.ok) {
    setRemoteFlags(validated.flags.entries, source, { notify });
    status.flags = 'ok';
    applied = true;
  } else {
    status.flags = 'rejected';
    messages.push(`flags rejected: ${validated.flags.errors.join('; ')}`);
  }
  status.message = messages.join(' | ');
  status.ignored = [
    ...validated.ignored.map((key) => `section ${key}`),
    ...(validated.experiments.ignored || []),
    ...(validated.flags.ignored || []),
  ];
  for (const message of messages) warnOnce(`${source}:${message}`, message);
  if (status.ignored.length) {
    warnOnce(
      `ignored:${status.ignored.join(',')}`,
      `remote config names things not in code, ignored: ${status.ignored.join(', ')}`
    );
  }
  return applied;
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(doc, fetchedAt) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt, doc }));
  } catch {
    // storage blocked
  }
}

// Applies the cached document synchronously, for the boot path. A cached
// document is validated exactly like a fresh one.
export function applyCachedRemoteConfig({
  experiments = EXPERIMENTS,
  flags = FLAGS,
  now = Date.now,
} = {}) {
  try {
    const cached = readCache();
    if (!cached || typeof cached.fetchedAt !== 'number') return false;
    const age = now() - cached.fetchedAt;
    if (age < 0 || age > CACHE_MAX_AGE_MS) return false;

    const validated = validateConfigDocument(cached.doc, {
      experiments,
      flags,
    });
    if (outcomeOf(cached.doc, validated).rejected) {
      warnOnce(
        'cached',
        `cached remote config rejected: ${[
          ...(validated.experiments.errors || []),
          ...(validated.flags.errors || []),
        ].join('; ')}`
      );
      return false;
    }
    const applied = applySections(validated, 'cached', experiments, true);
    if (applied) markFlagsSettled('cached');
    if (applied) status.fetchedAt = cached.fetchedAt;
    return applied;
  } catch {
    return false;
  }
}

// One non-blocking fetch. A document with at least one valid section is
// cached and held until the next route change; everything else fails open
// and leaves the config as it is.
export async function fetchRemoteConfig({
  fetchImpl,
  experiments = EXPERIMENTS,
  flags = FLAGS,
  now = Date.now,
} = {}) {
  try {
    const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
    if (!doFetch) {
      markFlagsSettled('remote');
      return false;
    }

    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
      : null;
    let response;
    try {
      response = await doFetch(`${RTDB_URL}${REMOTE_PATH}`, {
        cache: 'no-store',
        signal: controller ? controller.signal : undefined,
      });
    } finally {
      if (timer) clearTimeout(timer);
    }

    if (!response || !response.ok) {
      const message = `remote config HTTP ${response ? response.status : '?'}`;
      Object.assign(status, { fetch: 'failed', message });
      warnOnce('http', message);
      markFlagsSettled('remote');
      return false;
    }
    const doc = await response.json();
    const validated = validateConfigDocument(doc, { experiments, flags });
    const outcome = outcomeOf(doc, validated);
    if (outcome.rejected) {
      const message = `remote config rejected: ${[
        ...(validated.experiments.errors || []),
        ...(validated.flags.errors || []),
      ].join('; ')}`;
      Object.assign(status, { fetch: 'rejected', message });
      warnOnce('invalid', message);
      markFlagsSettled('remote');
      return false;
    }

    const fetchedAt = now();
    writeCache(doc, fetchedAt);
    pending = { validated, experiments };

    // Held UI has not rendered yet, so the flags section applies immediately
    // rather than waiting for a route change: there is nothing on screen to
    // flicker, and holding any longer would show the code default. Once
    // flags have settled (a cached document, or an earlier fetch), a newer
    // document waits for the next route change like everything else.
    if (!areFlagsSettled() && validated.flags.ok) {
      setRemoteFlags(validated.flags.entries, 'remote', { notify: true });
      status.flags = 'ok';
    }
    markFlagsSettled('remote');
    Object.assign(status, {
      fetch: outcome.partial ? 'partial' : 'ok',
      fetchedAt,
    });
    return true;
  } catch (err) {
    const message = `remote config unavailable: ${err?.message || err}`;
    Object.assign(status, { fetch: 'failed', message });
    warnOnce('fetch', message);
    markFlagsSettled('remote');
    return false;
  }
}

// Called on every route change, before the new route renders. Swaps in the
// document fetched since the last change, if any.
export function applyPendingRemoteConfig() {
  if (!pending) return false;
  const next = pending;
  pending = null;
  applySections(next.validated, 'remote', next.experiments, false);
  return true;
}

// Starts a fetch unless one was started recently. Fire and forget: the
// result lands in `pending` and applies on the next route change. Returns
// whether a fetch was actually started, which is what the tests assert on.
export function maybeRefetchRemoteConfig(options = {}) {
  try {
    const now = options.now || Date.now;
    const at = now();
    const last = readLastFetchStartedAt();
    // `last > at` means the clock moved backwards since the last fetch; treat
    // that as stale rather than letting it wedge the throttle for the session.
    const throttled =
      !options.force && last <= at && at - last < REFETCH_MIN_INTERVAL_MS;
    if (throttled) return false;
    writeLastFetchStartedAt(at);
    fetchRemoteConfig(options).catch(() => false);
    return true;
  } catch {
    return false;
  }
}

// A tab returning to the foreground, or restored from the back-forward
// cache, may have been away for hours. Both re-fetch; neither applies
// anything on its own, so the visitor sees no change until they navigate.
function watchForeground() {
  try {
    if (typeof document === 'undefined' || !document.addEventListener) return;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') maybeRefetchRemoteConfig();
    });
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('pageshow', (event) => {
        if (event && event.persisted) maybeRefetchRemoteConfig();
      });
    }
  } catch {
    // listeners are a convenience; never let them break boot
  }
}

// Boot: mint the visitor id, apply the cached document before first paint,
// then fetch a fresh one in the background. Never throws, never blocks.
export function initExperiments() {
  try {
    ensureUid();
    // With a usable cache, a reload moments after the last fetch can run on
    // it: the cache holds that very document, and it is applied before first
    // paint. Without one there is nothing to fall back on, so the boot fetch
    // ignores the throttle.
    const cacheApplied = applyCachedRemoteConfig();
    maybeRefetchRemoteConfig({ force: !cacheApplied });
    watchForeground();
  } catch {
    // fail open
  }
}

export function _resetRemoteConfigForTests() {
  pending = null;
  memoryLastFetchStartedAt = 0;
  try {
    sessionStorage.removeItem(FETCH_THROTTLE_KEY);
  } catch {
    // ignore
  }
  warned.clear();
  Object.assign(status, {
    fetch: 'idle',
    message: '',
    fetchedAt: null,
    ignored: [],
    applied: 'code',
    experiments: 'none',
    flags: 'none',
  });
  setExperiments(null);
  setRemoteFlags({}, 'code');
}
